import { apiGet } from './api';

export interface CurrentEventResponse {
  event_id: string;
  name: string;
  description: string;
  state: string;
  max_team_size: number;
  registration_deadline: string;
  submission_deadline_overview: string;
  submission_deadline_media: string;
  created_by: string;
  created_at: string;
  team_count: number;
  participant_count: number;
}

export interface UserTeamInfo {
  team_id: string;
  event_id: string;
  name: string;
  description: string;
  member_count: number;
  looking_for_members: boolean;
  created_at: string | null;
}

export interface UserProfileResponse {
  user_id: string;
  email: string;
  display_name: string;
  skills: string[];
  azure_ad_id: string | null;
  created_at: string;
  current_teams: UserTeamInfo[];
}

export interface DashboardStatusResponse {
  user_id: string;
  user_email: string;
  user_name: string;
  roles: string[];
  is_participant: boolean;
  is_organizer: boolean;
  is_judge: boolean;
  has_active_event: boolean;
  active_event_id: string | null;
  active_event_name: string | null;
  recommended_action: string | null;
  user_team_in_current_event: {
    has_team: boolean;
    team_id: string | null;
    team_name: string | null;
  } | null;
}

/**
 * Dashboard service for handling participant navigation and current event management
 */
export class DashboardService {
  
  /**
   * Get the current user's profile including team information
   */
  static async getUserProfile(token: string): Promise<UserProfileResponse> {
    return apiGet<UserProfileResponse>('/users/me', token);
  }
  
  /**
   * Get the current active event
   */
  static async getCurrentEvent(token: string): Promise<CurrentEventResponse> {
    return apiGet<CurrentEventResponse>('/events/current', token);
  }
  
  /**
   * Get participant dashboard (auto-redirects to current event)
   */
  static async getParticipantDashboard(token: string): Promise<CurrentEventResponse> {
    return apiGet<CurrentEventResponse>('/dashboard/participant', token);
  }
  
  /**
   * Get dashboard status and navigation information
   */
  static async getDashboardStatus(token: string): Promise<DashboardStatusResponse> {
    return apiGet<DashboardStatusResponse>('/dashboard/status', token);
  }
  
  /**
   * Get enhanced dashboard status including user team information from /users/me
   */
  static async getEnhancedDashboardStatus(token: string): Promise<DashboardStatusResponse & { 
    user_profile?: UserProfileResponse;
    user_team_in_current_event_enhanced?: {
      has_team: boolean;
      team_id: string | null;
      team_name: string | null;
      team_info?: UserTeamInfo;
    };
  }> {
    try {
      // Get both dashboard status and user profile in parallel
      const [dashboardStatus, userProfile] = await Promise.all([
        this.getDashboardStatus(token),
        this.getUserProfile(token)
      ]);
      
      // Find the team for the current active event
      let enhancedTeamInfo = null;
      if (dashboardStatus.active_event_id && userProfile.current_teams.length > 0) {
        console.log('🔍 Matching user teams to active event:', {
          activeEventId: dashboardStatus.active_event_id,
          userTeams: userProfile.current_teams.map(t => ({
            teamId: t.team_id,
            eventId: t.event_id,
            name: t.name
          }))
        });
        
        const teamInCurrentEvent = userProfile.current_teams.find(
          team => team.event_id === dashboardStatus.active_event_id
        );
        
        console.log('🎯 Team matching result:', {
          foundTeam: !!teamInCurrentEvent,
          teamDetails: teamInCurrentEvent ? {
            teamId: teamInCurrentEvent.team_id,
            eventId: teamInCurrentEvent.event_id,
            name: teamInCurrentEvent.name
          } : null
        });
        
        if (teamInCurrentEvent) {
          enhancedTeamInfo = {
            has_team: true,
            team_id: teamInCurrentEvent.team_id,
            team_name: teamInCurrentEvent.name,
            team_info: teamInCurrentEvent
          };
        }
      }
      
      return {
        ...dashboardStatus,
        user_profile: userProfile,
        user_team_in_current_event_enhanced: enhancedTeamInfo || {
          has_team: false,
          team_id: null,
          team_name: null,
          team_info: undefined
        }
      };
    } catch (error) {
      console.error('Failed to get enhanced dashboard status:', error);
      // Fallback to regular dashboard status
      return this.getDashboardStatus(token);
    }
  }
  
  /**
   * Determine the appropriate home page route based on user role and current state
   */
  static async getHomePageRoute(token: string): Promise<string> {
    try {
      const status = await this.getDashboardStatus(token);
      
      switch (status.recommended_action) {
        case 'view_team_dashboard':
          return `/events/${status.active_event_id}/teams/${status.user_team_in_current_event?.team_id}`;
        case 'create_or_join_team':
          return `/events/${status.active_event_id}/teams`;
        case 'view_organizer_dashboard':
          return `/events/${status.active_event_id}/admin`;
        case 'view_judge_dashboard':
          return `/events/${status.active_event_id}/judge`;
        case 'create_event':
          return '/events/create';
        case 'wait_for_event':
        default:
          return '/dashboard';
      }
    } catch (error) {
      console.error('Failed to get dashboard status:', error);
      return '/dashboard'; // Fallback
    }
  }
  
  /**
   * Check if there's an active event
   */
  static async hasActiveEvent(token: string): Promise<boolean> {
    try {
      await this.getCurrentEvent(token);
      return true;
    } catch (error) {
      // Expected when no active event exists (404)
      console.debug('No active event found:', error);
      return false;
    }
  }
  
  /**
   * Get navigation items based on user role and current state
   */
  static async getNavigationItems(token: string): Promise<Array<{
    label: string;
    route: string;
    icon?: string;
    active?: boolean;
  }>> {
    try {
      const status = await this.getDashboardStatus(token);
      const items: Array<{
        label: string;
        route: string;
        icon?: string;
        active?: boolean;
      }> = [];
      
      // Dashboard (always available)
      items.push({
        label: 'Dashboard',
        route: '/dashboard',
        icon: '🏠'
      });
      
      if (status.has_active_event) {
        // Event-specific navigation
        if (status.is_participant) {
          items.push({
            label: 'Current Event',
            route: `/events/${status.active_event_id}`,
            icon: '🎯'
          });
          
          items.push({
            label: 'Teams',
            route: `/events/${status.active_event_id}/teams`,
            icon: '👥'
          });
          
          if (status.user_team_in_current_event?.has_team) {
            items.push({
              label: 'My Team',
              route: `/events/${status.active_event_id}/teams/${status.user_team_in_current_event.team_id}`,
              icon: '⭐'
            });
          }
        }
        
        if (status.is_organizer) {
          items.push({
            label: 'Event Management',
            route: `/events/${status.active_event_id}/admin`,
            icon: '⚙️'
          });
        }
        
        if (status.is_judge) {
          items.push({
            label: 'Judging',
            route: `/events/${status.active_event_id}/judge`,
            icon: '⚖️'
          });
        }
      }
      
      // Global navigation for organizers
      if (status.is_organizer) {
        items.push({
          label: 'All Events',
          route: '/events',
          icon: '📅'
        });
        
        if (!status.has_active_event) {
          items.push({
            label: 'Create Event',
            route: '/events/create',
            icon: '➕'
          });
        }
      }
      
      return items;
    } catch (error) {
      console.error('Failed to get navigation items:', error);
      return [
        {
          label: 'Dashboard',
          route: '/dashboard',
          icon: '🏠'
        }
      ];
    }
  }
}
